import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, isRef as _isRef, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = { class: "font-bold dark:text-gray-300" }
const _hoisted_3 = {
  class: "p-2",
  style: {"background":"#F6EDD2","border-bottom":"1px solid #D8CEB5"}
}
const _hoisted_4 = { class: "text-sm" }
const _hoisted_5 = { class: "font-semibold" }
const _hoisted_6 = { class: "font-semibold" }
const _hoisted_7 = { class: "text-sm text-gray-500" }
const _hoisted_8 = {
  class: "p-2",
  style: {"background":"#F6EDD2","border-bottom":"1px solid #D8CEB5"}
}
const _hoisted_9 = { class: "text-sm" }
const _hoisted_10 = { class: "text-sm" }
const _hoisted_11 = { class: "text-sm" }
const _hoisted_12 = { class: "text-sm" }
const _hoisted_13 = { class: "text-sm capitalize" }
const _hoisted_14 = { class: "text-sm" }
const _hoisted_15 = { class: "text-sm" }
const _hoisted_16 = { class: "text-sm" }
const _hoisted_17 = { class: "text-sm" }

import { ref, computed, onBeforeMount } from 'vue';
import CreateActions from '@/components/cards/CreatePool/CreateActions.vue';
import AnimatePresence from '@/components/animate/AnimatePresence.vue';

import usePoolCreation from '@/composables/pools/usePoolCreation';
import useTokens from '@/composables/useTokens';
import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import useWeb3 from '@/services/web3/useWeb3';

import { useI18n } from 'vue-i18n';
import { bnum, shortenLabel } from '@/lib/utils';
import BigNumber from 'bignumber.js';

/**
 * PROPS & EMITS
 */

export default _defineComponent({
  emits: ["close", "success"] as unknown as undefined,
  setup(__props, { emit }: { emit: ({
  (e: 'close'): void;
  (e: 'success'): void;
}), expose: any, slots: any, attrs: any }) {



/**
 * STATE
 */

const poolCreated = ref(false);

/**
 * COMPOSABLES
 */
const {
  seedTokens,
  poolLiquidity,
  poolTypeString,
  initialFee,
  name: poolName,
  symbol: poolSymbol,
  useNativeAsset,
  feeManagementType,
  feeController,
  thirdPartyFeeController,
  createPoolTxHash,
  goBack,
  setActiveStep,
  sortSeedTokens,
  getScaledAmounts,
  saveState
} = usePoolCreation();

const { tokens, priceFor, nativeAsset, wrappedNativeAsset } = useTokens();
const { fNum2 } = useNumbers();
const { t } = useI18n();
const { userNetworkConfig, account } = useWeb3();

/**
 * LIFECYCLE
 */
onBeforeMount(() => {
  sortSeedTokens();
});

/**
 * COMPUTED
 */
const title = computed((): string =>
  poolCreated.value
    ? t('poolCreated')
    : t('previewPool', [poolTypeString.value])
);

// translations are breaking when directly using this label
const initialWeightLabel = computed(() => t('initialWeight'));

const tokenAddresses = computed((): string[] => {
  return seedTokens.value.map(token => {
    if (
      token.tokenAddress == wrappedNativeAsset.value.address &&
      useNativeAsset.value
    ) {
      return nativeAsset.address;
    }
    return token.tokenAddress;
  });
});

const tokenAmounts = computed((): string[] => {
  return getScaledAmounts();
});

const hasMissingPoolNameOrSymbol = computed(() => {
  return poolSymbol.value === '' || poolName.value === '';
});

const initialWeights = computed(() => {
  const _initialWeights: Record<string, BigNumber> = {};
  for (const seedToken of seedTokens.value) {
    _initialWeights[seedToken.tokenAddress] = bnum(seedToken.amount)
      .times(priceFor(seedToken.tokenAddress))
      .div(poolLiquidity.value);
  }
  return _initialWeights;
});

// an invalid initial weight is one where the the weight
// is less than 1% of the pools value
const hasInvalidInitialWeight = computed(() => {
  return Object.values(initialWeights.value).some(initialWeight =>
    initialWeight.lt(0.01)
  );
});

/**
 * METHODS
 */
function handleSuccess(): void {
  poolCreated.value = true;
  emit('success');
}

function navigateToPoolFee() {
  setActiveStep(1);
}

function getSwapFeeManager() {
  if (feeManagementType.value === 'governance') {
    return t('koilGovernance');
  } else {
    if (feeController.value === 'self') {
      return `${t('myWallet')}: ${shortenLabel(account.value)}`;
    } else {
      return shortenLabel(thirdPartyFeeController.value);
    }
  }
}

function getInitialWeightHighlightClass(tokenAddress: string) {
  return {
    'text-gray-500': initialWeights[tokenAddress]?.gte(0.01),
    'text-yellow-500': initialWeights[tokenAddress]?.lt(0.01)
  };
}

return (_ctx: any,_cache: any) => {
  const _component_BalIcon = _resolveComponent("BalIcon")!
  const _component_BalCircle = _resolveComponent("BalCircle")!
  const _component_BalStack = _resolveComponent("BalStack")!
  const _component_BalAsset = _resolveComponent("BalAsset")!
  const _component_BalCard = _resolveComponent("BalCard")!
  const _component_BalInlineInput = _resolveComponent("BalInlineInput")!
  const _component_BalAlert = _resolveComponent("BalAlert")!

  return (_openBlock(), _createBlock(_component_BalStack, {
    vertical: "",
    spacing: "xs",
    class: "mb-24"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_BalCard, {
        shadow: "xl",
        noBorder: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_BalStack, { vertical: "" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                (poolCreated.value)
                  ? (_openBlock(), _createBlock(_component_BalCircle, {
                      key: 0,
                      size: "8",
                      color: "green",
                      class: "text-white mr-2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_BalIcon, { name: "check" })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_BalStack, {
                  horizontal: "",
                  align: "center",
                  spacing: "xs"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(goBack) && _unref(goBack)(...args))),
                      class: "text-red-500 hover:text-red-700 flex"
                    }, [
                      _createVNode(_component_BalIcon, {
                        class: "flex",
                        name: "chevron-left"
                      })
                    ]),
                    _createElementVNode("h5", _hoisted_2, _toDisplayString(_unref(title)), 1)
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_component_BalCard, {
                shadow: "none",
                noPad: ""
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("h6", _hoisted_4, _toDisplayString(_ctx.$t('createAPool.tokensAndSeedLiquidity')), 1)
                  ]),
                  _createVNode(_component_BalStack, {
                    vertical: "",
                    spacing: "none",
                    withBorder: ""
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(seedTokens), (token) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `tokenpreview-${token.tokenAddress}`,
                          class: "p-4"
                        }, [
                          _createVNode(_component_BalStack, {
                            horizontal: "",
                            justify: "between"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_BalStack, {
                                horizontal: "",
                                align: "center"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_BalAsset, {
                                    address: token.tokenAddress,
                                    size: 36
                                  }, null, 8, ["address"]),
                                  _createVNode(_component_BalStack, {
                                    vertical: "",
                                    spacing: "none"
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(fNum2)(token.weight / 100, _unref(FNumFormats).percent)) + " " + _toDisplayString(_unref(tokens)[token.tokenAddress]?.symbol), 1),
                                      _createElementVNode("span", {
                                        class: _normalizeClass([
                        'text-sm',
                        getInitialWeightHighlightClass(token.tokenAddress)
                      ])
                                      }, _toDisplayString(_unref(initialWeightLabel)) + ": " + _toDisplayString(_unref(fNum2)(
                          _unref(initialWeights)[token.tokenAddress].toString(),
                          _unref(FNumFormats).percent
                        )), 3)
                                    ]),
                                    _: 2
                                  }, 1024)
                                ]),
                                _: 2
                              }, 1024),
                              _createVNode(_component_BalStack, {
                                vertical: "",
                                spacing: "none",
                                align: "end"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(fNum2)(token.amount, _unref(FNumFormats).token)), 1),
                                  _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(fNum2)(
                        _unref(bnum)(token.amount)
                          .times(_unref(priceFor)(token.tokenAddress))
                          .toString(),
                        _unref(FNumFormats).fiat
                      )), 1)
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1024)
                        ]))
                      }), 128))
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_BalStack, {
                    horizontal: "",
                    justify: "between",
                    class: "p-4 border-t",
                    style: {"border-color":"#D8CEB5"}
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("h6", null, _toDisplayString(_ctx.$t('total')), 1),
                      _createElementVNode("h6", null, _toDisplayString(_unref(fNum2)(_unref(poolLiquidity).toString(), _unref(FNumFormats).fiat)), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_BalCard, {
                shadow: "none",
                noPad: ""
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("h6", _hoisted_9, _toDisplayString(_ctx.$t('summary')), 1)
                  ]),
                  _createVNode(_component_BalStack, {
                    vertical: "",
                    spacing: "xs",
                    class: "p-3"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_BalStack, {
                        horizontal: "",
                        justify: "between"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t('poolSymbol')) + ":", 1),
                          _createVNode(_component_BalInlineInput, {
                            size: "xs",
                            modelValue: _unref(poolName),
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(poolName) ? poolName.value = $event : null)),
                            onSave: _unref(saveState),
                            inputAlignRight: ""
                          }, null, 8, ["modelValue", "onSave"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_BalStack, {
                        horizontal: "",
                        justify: "between"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t('poolName')) + ":", 1),
                          _createVNode(_component_BalInlineInput, {
                            size: "xs",
                            modelValue: _unref(poolSymbol),
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(poolSymbol) ? poolSymbol.value = $event : null)),
                            onSave: _unref(saveState),
                            inputAlignRight: ""
                          }, null, 8, ["modelValue", "onSave"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_BalStack, {
                        horizontal: "",
                        justify: "between"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t('poolType')) + ":", 1),
                          _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(poolTypeString)), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_BalStack, {
                        horizontal: "",
                        justify: "between",
                        class: "mt-1"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.$t('swapFee')) + ":", 1),
                          _createVNode(_component_BalStack, {
                            horizontal: "",
                            spacing: "sm"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(fNum2)(_unref(initialFee), _unref(FNumFormats).percent)), 1),
                              _createElementVNode("button", {
                                class: "hover:text-red-500",
                                onClick: navigateToPoolFee
                              }, [
                                _createVNode(_component_BalIcon, {
                                  name: "edit",
                                  size: "xs"
                                })
                              ])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_BalStack, {
                        horizontal: "",
                        justify: "between"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_16, _toDisplayString(_ctx.$t('swapFeeManager')) + ":", 1),
                          _createVNode(_component_BalStack, {
                            horizontal: "",
                            spacing: "sm"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_17, _toDisplayString(getSwapFeeManager()), 1),
                              _createElementVNode("button", {
                                class: "hover:text-red-500",
                                onClick: navigateToPoolFee
                              }, [
                                _createVNode(_component_BalIcon, {
                                  name: "edit",
                                  size: "xs"
                                })
                              ])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(AnimatePresence, {
                isVisible: _unref(hasMissingPoolNameOrSymbol),
                unmountInstantly: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_BalAlert, {
                    title: _ctx.$t('missingPoolNameOrSymbol'),
                    type: "error"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('missingPoolNameOrSymbolInfo')), 1)
                    ]),
                    _: 1
                  }, 8, ["title"])
                ]),
                _: 1
              }, 8, ["isVisible"]),
              _createVNode(AnimatePresence, {
                isVisible: _unref(hasInvalidInitialWeight) && _unref(createPoolTxHash) !== '',
                unmountInstantly: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_BalAlert, {
                    title: _ctx.$t('createAPool.invalidInitialWeightsTitle'),
                    type: "warning"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('createAPool.invalidInitialWeightsInfo')), 1)
                    ]),
                    _: 1
                  }, 8, ["title"])
                ]),
                _: 1
              }, 8, ["isVisible"]),
              _createVNode(CreateActions, {
                createDisabled: _unref(hasMissingPoolNameOrSymbol),
                tokenAddresses: _unref(tokenAddresses),
                amounts: _unref(tokenAmounts),
                onSuccess: handleSuccess
              }, null, 8, ["createDisabled", "tokenAddresses", "amounts"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})